import React from 'react';
import { Navigate } from 'react-router-dom';
import { Server } from '../../server/server';
import AlertModal from '../modals/AlertModal';
import { BsExclamationCircleFill } from 'react-icons/bs';
import PageEnvelope from '../elements/PageEnvelope';
import Portrait from '../elements/Portrait';
import './NotificationsPage.css'

interface NotificationsPageState {
  messages: any[];
  loading: boolean;
  loadingMoreMessages: boolean;
  noMoreMessages: boolean;
  navigate: string;
  alert: string;
  newNotifications: boolean;
  loadingNewNotifications: boolean;
}

class NotificationsPage extends React.Component<{}, NotificationsPageState> {
  monitorInterval: number;
  monitorSchedule: number;

  constructor(props: any) {
    super(props);

    this.state = {
      messages: [],
      navigate: '',
      loading: false,
      loadingMoreMessages: false,
      noMoreMessages: false,
      alert: '',
      newNotifications: false,
      loadingNewNotifications: false
    }
  }

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    this.stopMonitoring();
  }

  async load() {
    await this.loadMessages();
    this.monitorSchedule = setTimeout(() => {
      this.startMonitoring();
    }, 1000);
  }

  async loadMessages() {
    let cachedMessages = await Server.notifications.getMessagesCache();

    let updating = false;
    if(cachedMessages) {
      let profiles = this.getProfilesInMessages(cachedMessages);
      profiles = profiles.filter((profile:any)=>Server.public.getProfile(profile) == null);
      if(profiles.length > 0) {
        this.setState({loading: true});
        await Server.public.loadProfiles(profiles);
      }

      this.setState({messages: cachedMessages, loading: false, loadingNewNotifications: false});

      if(cachedMessages.length > 0) 
        Server.monitor.setNotificationsLastRead(cachedMessages[0].date)

      updating = cachedMessages.length > 0;
      let notificationsRefreshTime = window.sessionStorage.getItem(`NotificationsRefresh`);
      if(notificationsRefreshTime) {
        let refreshTime = parseInt(notificationsRefreshTime);
        let now = new Date().getTime();
        if(now - refreshTime < 1000 * 60) 
          return;
      }
    }

    let params:any = {limit: 10};

    if(updating)
      params.after = cachedMessages[0].date;

    this.setState({loading: true});

    let response = await Server.notifications.getMessages(params);

    this.setState({loading: false, loadingNewNotifications: false});

    if(!response.success) {
      this.setState({alert: response.message});
      return;
    }

    window.sessionStorage.setItem(`NotificationsRefresh`, new Date().getTime().toString());
    
    let newMessages = response.messages;

    if(newMessages.length == 0 && updating) 
      return;

    this.setState({loading: true});

    await this.loadFriendsFromNewMessages(newMessages);

    let profiles = this.getProfilesInMessages(newMessages);
    await Server.public.loadProfiles(profiles);

    let messages = [];
    if(updating)
      messages = [...cachedMessages];

    messages = this.addNewMessagesToExistingMessages(newMessages, messages);

    await Server.notifications.setMessagesCache(messages, updating);

    if(!updating) {
      let noMoreMessages = messages.length < 10;
      this.setState({noMoreMessages: noMoreMessages});
    }

    this.setState({messages, loading: false, loadingNewNotifications: false, newNotifications: false});

    if(messages.length > 0) 
      await Server.monitor.setNotificationsLastRead(messages[0].date);
  }

  async loadMoreMessages() {
    this.setState({loadingMoreMessages: true});

    let params:any = {limit: 10};

    let message = this.state.messages[this.state.messages.length-1];
    params.before = message.date;

    let response = await Server.notifications.getMessages(params);

    if(!response.success) {
      this.setState({loadingMoreMessages: false, alert: response.message});
      return;
    }

    let newMessages = response.messages;

    if(newMessages.length == 0) {
      this.setState({loadingMoreMessages: false, noMoreMessages: true});
      return;
    }

    let profiles = this.getProfilesInMessages(newMessages);
    await Server.public.loadProfiles(profiles);

    let messages = this.addNewMessagesToExistingMessages(newMessages, this.state.messages);

    await Server.notifications.setMessagesCache(messages, true);

    let noMoreMessages = messages.length < 10;

    this.setState({messages, noMoreMessages, loadingMoreMessages: false});
  }

  async loadFriendsFromNewMessages(messages:any[]) {
    let loadFriends = false;
    for(let i = 0; i < messages.length; i++) {
      let message = messages[i];
      if(message.type == 'friend-invite' || message.type == 'friend-accept' || message.type == 'friend-decline' || message.type == 'friend-remove') 
        loadFriends = true;
    }

    if(loadFriends) 
      await Server.user.getFriends();
  }

  getProfilesInMessages(messages:any[]) {
    let profiles = [];
    for (let i = 0; i < messages.length; i++) {
      let user = messages[i].user;
      if(user && profiles.indexOf(user) == -1)
        profiles.push(user);
    }
    return profiles;
  }

  addNewMessagesToExistingMessages(newMessages:any[], existingMessages:any[]) {
    let messages = [...existingMessages];

    for (let newMessage of newMessages) {
      let existingIndex = messages.findIndex(message => 
        message.user === newMessage.user && message.date === newMessage.date
      );
      
      if (existingIndex !== -1) 
        messages[existingIndex] = newMessage;
      else
        messages.push(newMessage);
    }

    this.sortMessages(messages);

    return messages;
  }

  sortMessages(messages:any[]) {
    messages.sort((a:any, b:any)=>{
      if(a.date > b.date)
        return -1;
      else if(a.date < b.date)
        return 1;
      return 0;
    })
  }

  startMonitoring() {
    this.monitorInterval = setInterval(() => {
      const currentNewNotifications = Server.monitor.isNewNotifications();
      if (currentNewNotifications !== this.state.newNotifications) 
        this.setState({ newNotifications: currentNewNotifications });
    }, 250); 
  }

  stopMonitoring() {
    if(this.monitorSchedule) {
      clearTimeout(this.monitorSchedule);
      this.monitorSchedule = null;
    }

    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }
  }

  async onShowNewNotifications() {
    this.stopMonitoring();
    this.setState({loadingNewNotifications: true});
    await this.load();
    window.scrollTo(0, 0);
  }

  onLoadMore() {
    this.loadMoreMessages();
  }

  onMessageClick(msg:any) {
    switch(msg.type) {
      case 'blog-comment':
      case 'blog-comment-comment':
      case 'blog-publish':
        this.onBlogCommentClick(msg);
        break;      
      case 'forum-post':
      case 'forum-post-post':
        window.sessionStorage.removeItem(`ForumTopicRefresh#${msg.forum}#${msg.topic}`);
        this.setState({navigate: `/forum/${msg.forum}/${msg.topic}`})
        break;      
      case 'forum-new-topic':
        this.setState({navigate: `/forum/${msg.forum}/${msg.topic}`})
        break;      
      case 'post-reply':
      case 'post-reply-reply':
        window.sessionStorage.removeItem(`ActivityPostReplyRefresh#${msg.post}#false`);
        window.sessionStorage.removeItem(`ActivityPostReplyRefresh#${msg.post}#true`);
        this.setState({navigate: '/activity/post/' + msg.post})
        break;      
      case 'post-like':
      case 're-post':
        this.setState({navigate: '/activity/post/' + msg.post})
        break;      
      case 'friend-invite':
      case 'friend-accept':
      case 'friend-decline':
      case 'friend-remove':
        window.sessionStorage.removeItem(`FriendRefresh`);
        this.setState({navigate: '/friends'})
        break;
      case 'follow':
      case 'unfollow':
        let profile = Server.public.getProfile(msg.user);
        if(profile)
          this.setState({navigate: `/profile/${profile.slug}`})
        break;      
    }
  }

  async onBlogCommentClick(msg:any) {
    let blog = await Server.blogs.getBlogFromCacheBySlug(msg.slug);

    if(blog) {
      window.sessionStorage.removeItem(`BlogCommentRefresh#${blog.id}#false`);
      window.sessionStorage.removeItem(`BlogCommentRefresh#${blog.id}#true`);
    }

    this.setState({navigate: '/blog/' + msg.slug})
  }

  renderMessages() {
    if(this.state.loading) 
      return (<div className="site-page-panel">Loading...</div>); 

    let lastRead = Server.monitor.getNotificationsLastRead(); 
    let lastReadDate = lastRead ? new Date(lastRead) : null;

    let divs = [];
    for (let i = 0; i < this.state.messages.length; i++) {
      let message = this.state.messages[i];

      let user = null;
      if(message.type == 'event-win')
        user = Server.user.getProfile();
      else
        user = Server.public.getProfile(message.user);

      let userName = user ? user.name : 'Deleted User';
      if(message.type == 'event-win')
        userName = 'You';

      // parse the content of notification
      let title = '';
      let content = '';

      switch(message.type) {
        case 'post-like':
          title = 'liked your activity';
          content = message.content;
          break;
        case 'post-reply':
          title = 'replied to your activity';
          content = `"${message.content}"`;
          break;
        case 'post-reply-reply':
          title = 'replied to activity you also replied to';
          content = `"${message.content}"`;
          break;
        case 're-post':
          title = 'reposted your activity';
          content = message.content;
          break;
        case 'forum-post':
          title = 'posted in your forum topic';
          content = `"${message.post}"`;
          break;
        case 'forum-post-post':
          title = 'posted in a forum topic you also posted in';
          content = `"${message.post}"`;
          break;
        case 'forum-new-topic':
          title = 'created a new forum topic';
          content = `"${message.title}"`;
          break;
        case 'blog-comment':
          title = 'commented your blog';
          content = `"${message.comment}"`;
          break;
        case 'blog-comment-comment':
          title = 'commented on a blog you also commented on';
          content = `"${message.comment}"`;
          break;
        case 'blog-publish':
          title = 'published a new blog';
          content = `"${message.title}"`;
          break;
        case 'friend-accept':
          title = 'accepted your friend request';
          break;
        case 'friend-invite':
          title = 'invited you to be their friend';
          break;
        case 'friend-decline':
          title = 'declined your friend request';
          break;
        case 'friend-remove':
          title = 'is no longer your friend';
          break;
        case 'follow':
          title = 'followed you';
          break;
        case 'unfollow':
          title = 'stopped following you';
          break;
        case 'user-joined':
          title = ' has joined your community';
          break;
        case 'site-invite-accept':
          title = ' has accepted your invitation and joined the community';
          break;
        }

      let unread = false;
      if(lastReadDate) {
        let msgDate = new Date(message.date);
        unread = msgDate > lastReadDate;
      }

      divs.push(
        <div key={i} className="site-page-panel" style={{rowGap: '0px', cursor: 'pointer'}} onClick={()=>this.onMessageClick(message)}>
          <div className="site-page-row" style={{justifyContent: 'space-between'}}>
            <div style={{display: 'grid', gridTemplateColumns: 'auto 1fr 15px', alignItems: 'center'}}>
              {user && 
                <Portrait user={user.id} />
              }
              <div style={{marginLeft: '8px'}}>
                <div><b>{userName}</b> {title}</div>
                {content != '' &&
                  <div className="site-page-subtext" style={{marginLeft: '10px', marginTop:'5px'}}>{content}</div>
                }
              </div>
            </div>
            {unread && 
              <div className="site-page-notify-circle">
                <BsExclamationCircleFill size="1.4em" />
              </div>
            }
          </div>
        </div>
      );
    }

    return divs.length > 0 
      ? <div style={{display: 'flex', flexDirection: 'column', rowGap: '10px'}}>
          {this.state.newNotifications &&
            <div style={{position: 'sticky', top: '70px', zIndex: '19', cursor: 'pointer'}}>
              <div className="site-page-notification-panel" onClick={()=>this.onShowNewNotifications()}>
                {this.state.loadingNewNotifications ? 'Loading...' : 'Tap here to show new notifications.'}
              </div>
            </div>
          }            

          {divs}

          {!this.state.loading && !this.state.noMoreMessages &&
            <div style={{marginTop: '15px'}}>
              <button
                onClick={()=>this.onLoadMore()}
                disabled={this.state.loadingMoreMessages}
              >
                {this.state.loadingMoreMessages ? 'Loading...' : 'Load More'}
              </button>
            </div>
          }
        </div>
      : <div className="site-page-panel">No notifications yet.</div>
  }

  render() {
    if(!Server.account.isLoggedIn())
      return(<Navigate replace to="/" />);

    if(this.state.navigate != '')
      return <Navigate to={this.state.navigate} />;

    return (
      <div className="site-page">
        <PageEnvelope width="500px">
          {this.state.loading ? 
            <div className="site-page-panel">Loading...</div> : 
            this.renderMessages()
          }
        </PageEnvelope>
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
      </div>
    )
  }
}

export default NotificationsPage;